import types from "./types";
import { VIEW_BUNDLES, PRICING_LIST_PLANS } from "./screen";
import { PLAN_STATUS, PLAN_VISIBILITY, ADVANCE_PLAN_CHANGE } from "../Utils/constants";

const getFormattedNumber = (raw) => {
    if (!raw && raw !== 0) return "";
    raw = String(raw);
    const parts = raw.split(".");
    if (parts.length > 2) {
        raw = parts[0] + "." + parts.slice(1).join("");
    }
    let formatted = raw;
    if (raw.includes(".")) {
        const [integerPart, decimalPart] = raw.split(".");
        const limitedDecimalPart = decimalPart.slice(0, 2);
        const formattedInteger = integerPart.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ",",
        );
        formatted = formattedInteger + "." + limitedDecimalPart;
    } else {
        formatted = raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    return formatted;
}

const getInitialBundlePlan = () => ({
    planId: null,
    planName: "",
    visibility: PLAN_VISIBILITY.PRIVATE,
    isInstallFeeEnabled: false,
    installFee: "",
    installFeeCatalogNumber: "",
    planType: {
        label: "",
        value: "",
    },
    monthCost: "",
    planCatalogNumber: "",
    overageCatalogNumber: "",
    basic: {
        isActive: false,
        title: "Basic",
        baseValue: "0",
        description: "Add a promotional copy about this pricing plan",
        radioValue: "nobucket",
        containers: [],
    },
    plus: {
        isActive: false,
        title: "Plus",
        baseValue: "0",
        description: "Add a promotional copy about this pricing plan",
        radioValue: "nobucket",
        containers: [],
    },
    pro: {
        isActive: false,
        title: "Pro",
        baseValue: "0",
        description: "Add a promotional copy about this pricing plan",
        radioValue: "nobucket",
        containers: [],
    },
    custom: {
        title: "Custom",
        baseValue: "0",
        description: "Add a promotional copy about this pricing plan",
        radioValue: "nobucket",
        containers: [],
    },
});

const apiBundleManagementInitialState = {
    screen: VIEW_BUNDLES,

    showSuccessToast1: false,
    toastMessage1: "",

    fetchingBundleList: false,
    bundleList: [],
    unAuthorizedUser: true,

    apis: [],
    fetchingApis: false,
    errorFetchingApis: false,

    bundlePricingPlans: [],
    fetchingBundlePricingPlans: false,
    errorFetchingBundlePricingPlans: false,
    errorFetchingBundlePricingPlansMessage: null,

    fetchingPlanDetails: false,
    planDetails: null,
    errorFetchingPlanDetails: false,
    errorFetchingPlanDetailsMessage: null,

    creatingOrEditingPlan: false,
    addOrEditPricePlanError: null,
    showAddEditPriceErrorDialog: false,

    creatingBundle: false,
    bundleCreateError: false,
    bundleCreateErrorMessage: null,
    selectedPricingPlan: null,

    updatingBundle: false,
    bundleUpdateError: false,
    bundleUpdateErrorMessage: null,

    selectedPricePlanForHistory: null,
    prevScreenForHistory: null,

    bundlePlan: getInitialBundlePlan(),

    selectedSubPlan: null,
    fetchingEndpointsAndChannels: false,
    errorFetchingEndpointsAndChannels: false,
    errorMessageFetchingEndpointsAndChannels: null,

    bundleApiEndpoints: {},
    bundleApiChannels: {},

    // plan containers map new state
    subPlanContainers: {},
    selectedSubPlanContainer: null,

    selectedBundle: null,
    endpointsBundleId: null,
    draftSubPlanState: null,
};

const reducerActions = {
    [types.SET_SCREEN]: (state, action) => {
        const newScreen = action.payload;
        let additionalState = {};

        // Auto-clear active editing state when returning to list screens to prevent state bleed
        if (newScreen === PRICING_LIST_PLANS || newScreen === VIEW_BUNDLES) {
            additionalState = {
                bundlePlan: getInitialBundlePlan(),
                selectedPricingPlan: null,
                planDetails: null,
            };
        }

        return {
            ...state,
            screen: newScreen,
            ...additionalState
        }
    },
    [types.GET_BUNDLE_LIST_REQUEST]: (state, action) => {
        return {
            ...state,
            fetchingBundleList: true,
        }
    },
    [types.GET_BUNDLE_LIST_RECEIVE]: (state, action) => {
        return {
            ...state,
            fetchingBundleList: false,
            unAuthorizedUser: false,
            bundleList: (action.payload.items || []).sort(
                (a, b) => new Date(b.updated) - new Date(a.updated)
            ),
        }
    },
    [types.GET_BUNDLE_LIST_ERROR]: (state, action) => {
        return {
            ...state,
            fetchingBundleList: false,
            unAuthorizedUser: true,
        }
    },

    [types.GET_API_LIST_REQUEST]: (state, action) => {
        return {
            ...state,
            fetchingApis: true,
            errorFetchingApis: false,
        }
    },
    [types.GET_API_LIST_RECEIVE]: (state, action) => {
        return {
            ...state,
            fetchingApis: false,
            errorFetchingApis: false,
            apis: Object.values(action.payload) || [],
        }
    },
    [types.GET_API_LIST_ERROR]: (state, action) => {
        return {
            ...state,
            fetchingApis: false,
            errorFetchingApis: true,
            apis: [],
        }
    },

    [types.CREATE_BUNDLE_POST_REQUEST]: (state, action) => {
        return {
            ...state,
            creatingBundle: true,
            bundleCreateError: false,
            bundleCreateErrorMessage: null,
        }
    },
    [types.CREATE_BUNDLE_POST_RECEIVE]: (state, action) => {
        return {
            ...state,
            screen: VIEW_BUNDLES,
            creatingBundle: false,
            bundleCreateError: false,
            bundleCreateErrorMessage: null,
            showSuccessToast1: true,
            toastMessage1: "Your bundle has been created and added to the list.",
        }
    },
    [types.CREATE_BUNDLE_POST_ERROR]: (state, action) => {
        return {
            ...state,
            creatingBundle: false,
            bundleCreateError: true,
            bundleCreateErrorMessage: action.payload?.message || "Failed to create the bundle",
        }
    },

    [types.UPDATE_BUNDLE_PUT_REQUEST]: (state, action) => {
        return {
            ...state,
            updatingBundle: true,
            bundleUpdateError: false,
            bundleUpdateErrorMessage: null,
        }
    },
    [types.UPDATE_BUNDLE_PUT_RECEIVE]: (state, action) => {
        return {
            ...state,
            screen: VIEW_BUNDLES,
            updatingBundle: false,
            bundleUpdateError: false,
            bundleUpdateErrorMessage: null,
            showSuccessToast1: true,
            toastMessage1: <div>Your bundle <strong>{action.payload.name}</strong> has been updated.</div>,
        }
    },
    [types.UPDATE_BUNDLE_PUT_ERROR]: (state, action) => {
        return {
            ...state,
            updatingBundle: false,
            bundleUpdateError: true,
            bundleUpdateErrorMessage: action.payload?.message || "Failed to edit the bundle",
        }
    },
    [types.CLEAR_BUNDLE_UPDATE_ERRORS]: (state, action) => {
        return {
            ...state,
            bundleUpdateError: false,
            bundleUpdateErrorMessage: null,
        }
    },

    [types.GET_BUNDLE_PRICE_PLANS_REQUEST]: (state, action) => {
        return {
            ...state,
            fetchingBundlePricingPlans: true,
            errorFetchingBundlePricingPlans: false,
            errorFetchingBundlePricingPlansMessage: null,
        }
    },
    [types.GET_BUNDLE_PRICE_PLANS_RECEIVE]: (state, action) => {
        return {
            ...state,
            fetchingBundlePricingPlans: false,
            bundlePricingPlans: Object.values(action.payload) || [],
            errorFetchingBundlePricingPlans: false,
            errorFetchingBundlePricingPlansMessage: null,
        }
    },
    [types.GET_BUNDLE_PRICE_PLANS_ERROR]: (state, action) => {
        return {
            ...state,
            fetchingBundlePricingPlans: false,
            bundlePricingPlans: [],
            errorFetchingBundlePricingPlans: true,
            errorFetchingBundlePricingPlansMessage: action.payload?.message || "Failed to fetch bundle pricing plans",
        }
    },
    [types.RESET_BUNDLE_PRICE_PLANS]: (state, action) => {
        return {
            ...state,
            bundlePricingPlans: [],
            fetchingBundlePricingPlans: false,
            errorFetchingBundlePricingPlans: false,
            errorFetchingBundlePricingPlansMessage: null,
            selectedPricingPlan: null,
            selectedBundle: null,
            planDetails: null,
        }
    },
    [types.RESET_SELECTED_PRICING_PLAN]: (state, action) => {
        return {
            ...state,
            selectedPricingPlan: null,
            planDetails: null,
        }
    },
    [types.RESET_BUNDLE_PLAN]: (state, action) => {
        return {
            ...state,
            bundlePlan: getInitialBundlePlan(),
        }
    },

    [types.CREATE_BUNDLE_PRICE_PLAN_REQUEST]: (state, action) => {
        return {
            ...state,
            creatingOrEditingPlan: true,
        }
    },
    [types.CREATE_BUNDLE_PRICE_PLAN_RECEIVE]: (state, action) => {
        const isEditMode = state.selectedPricingPlan && state.selectedPricingPlan?.id;
        const existingPublicPlan =
            !isEditMode && action.payload.visibility === PLAN_VISIBILITY.PUBLIC
                ? state.bundlePricingPlans?.find(
                    (plan) =>
                        plan.visibility === PLAN_VISIBILITY.PUBLIC &&
                        plan.status === PLAN_STATUS.ACTIVE,
                )
                : null;
        return {
            ...state,
            creatingOrEditingPlan: false,
            screen: PRICING_LIST_PLANS,
            selectedPricingPlan: null,
            planDetails: null,
            showSuccessToast1: true,
            toastMessage1: !!existingPublicPlan && Object.values(existingPublicPlan).length ? (
                <div>
                    <strong>{action.payload.planName}</strong> is the new public plan
                    as the existing <strong>{existingPublicPlan.planName}</strong> has
                    been retired.
                </div>
            ) : (
                <div>
                    The plan <strong>{action.payload.planName}</strong> has been
                    successfully added.
                </div>
            ),
            bundlePlan: getInitialBundlePlan(),
        };
    },
    [types.CREATE_BUNDLE_PRICE_PLAN_ERROR]: (state, action) => {
        return {
            ...state,
            creatingOrEditingPlan: false,
            addOrEditPricePlanError: action.payload?.message || "Failed to create the pricing plan",
            showAddEditPriceErrorDialog: true,
        }
    },

    [types.UPDATE_BUNDLE_PRICE_PLAN_REQUEST]: (state, action) => {
        return {
            ...state,
            creatingOrEditingPlan: true,
        }
    },
    [types.UPDATE_BUNDLE_PRICE_PLAN_RECEIVE]: (state, action) => {
        return {
            ...state,
            creatingOrEditingPlan: false,
            screen: PRICING_LIST_PLANS,
            selectedPricingPlan: null,
            planDetails: null,
            showSuccessToast1: true,
            toastMessage1: (
                <div>
                    The plan <strong>{action.payload.planName}</strong> has been updated
                    successfully.
                </div>
            ),
            bundlePlan: getInitialBundlePlan(),
        }
    },
    [types.UPDATE_BUNDLE_PRICE_PLAN_ERROR]: (state, action) => {
        return {
            ...state,
            creatingOrEditingPlan: false,
            addOrEditPricePlanError: action.payload?.message || "Failed to update the pricing plan",
            showAddEditPriceErrorDialog: true,
        }
    },

    [types.FETCH_PLAN_DETAILS_REQUEST]: (state, action) => {
        return {
            ...state,
            fetchingPlanDetails: true,
            planDetails: null,
            errorFetchingPlanDetails: false,
            errorFetchingPlanDetailsMessage: null,
        }
    },
    [types.FETCH_PLAN_DETAILS_RECEIVE]: (state, action) => {
        return {
            ...state,
            fetchingPlanDetails: false,
            planDetails: action.payload,
            errorFetchingPlanDetails: false,
            errorFetchingPlanDetailsMessage: null,
        }
    },
    [types.FETCH_PLAN_DETAILS_ERROR]: (state, action) => {
        return {
            ...state,
            fetchingPlanDetails: false,
            planDetails: null,
            errorFetchingPlanDetails: true,
            errorFetchingPlanDetailsMessage: action.payload?.message || "Failed to fetch plan details",
        }
    },
    [types.CLEAR_FETCH_PLAN_DETAILS_ERROR]: (state, action) => {
        return {
            ...state,
            errorFetchingPlanDetails: false,
            errorFetchingPlanDetailsMessage: null,
        }
    },
    [types.LOAD_SUB_PLANS_FROM_DETAILS]: (state, action) => {
        const subPlans = action.payload || [];
        const updatedBundlePlan = { ...state.bundlePlan };

        subPlans.forEach(subPlan => {
            const planKey = subPlan.plan; // 'basic', 'plus', 'pro', or 'custom'

            if (planKey && updatedBundlePlan[planKey]) {
                // Transform containers from API format to Redux format
                const transformedContainers = (subPlan.containers || []).map(container => ({
                    id: container.id,
                    name: container.name,
                    description: container.description,
                    quotaLimit: container.quotaLimit != null ? container.quotaLimit.toString() : (container.quota != null ? container.quota.toString() : (container.monthlyQuota != null ? container.monthlyQuota.toString() : "0")),
                    overages: container.overages != null ? container.overages.toString() : (container.overage != null ? container.overage.toString() : (container.overageFee != null ? container.overageFee.toString() : "0")),
                    endpoints: container.endPoints || container.endpoints || [],
                    channels: (container.channels || []).map(ch => ({ ...ch, id: ch.channelId })),
                }));

                updatedBundlePlan[planKey] = {
                    ...updatedBundlePlan[planKey],
                    priceId: subPlan.priceId, // Preserve priceId for update API calls
                    isActive: subPlan.isActive ?? true,
                    title: subPlan.name || updatedBundlePlan[planKey].title,
                    baseValue: subPlan.price != null ? subPlan.price.toString() : updatedBundlePlan[planKey].baseValue,
                    description: subPlan.description || updatedBundlePlan[planKey].description,
                    containers: transformedContainers,
                    radioValue: transformedContainers.length > 1 ||
                        (transformedContainers.length === 1 && transformedContainers[0].name !== "All Endpoints")
                        ? "bucket" : "nobucket"
                };
            }
        });

        return {
            ...state,
            bundlePlan: updatedBundlePlan,
        };
    },

    [types.CLEAR_ADD_EDIT_PRICE_PLAN_ERROR]: (state, action) => {
        return {
            ...state,
            addOrEditPricePlanError: null,
            showAddEditPriceErrorDialog: false,
        }
    },

    [types.HANDLE_PLAN_NAME_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                planName: action.payload,
            }
        }
    },
    [types.HANDLE_PLAN_ID_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                planId: action.payload,
            }
        }
    },
    [types.HANDLE_PLAN_VISIBILITY_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                visibility: action.payload,
            }
        }
    },
    [types.HANDLE_INSTALL_ENABLE_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                isInstallFeeEnabled: action.payload,
            }
        }
    },
    [types.HANDLE_INSTALL_FEE_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                installFee: getFormattedNumber(action.payload),
            }
        }
    },
    [types.HANDLE_INSTALL_FEE_CATALOG_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                installFeeCatalogNumber: action.payload,
            }
        }
    },
    [types.HANDLE_PLAN_TYPE_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                planType: action.payload,
            }
        }
    },
    [types.HANDLE_MONTH_FEE_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                monthCost: getFormattedNumber(action.payload),
            }
        }
    },
    [types.HANDLE_PLAN_CATALOG_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                planCatalogNumber: action.payload,
            }
        }
    },
    [types.HANDLE_PLAN_OVERAGE_CATALOG_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                overageCatalogNumber: action.payload,
            }
        }
    },
    [types.HANDLE_PLAN_TOGGLE_CHANGE]: (state, action) => {
        const { planName, isActive } = action.payload;
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                [planName]: {
                    ...state.bundlePlan[planName],
                    isActive: isActive,
                }
            }
        }
    },
    [types.SET_SELECTED_PRICING_PLAN]: (state, action) => {
        return {
            ...state,
            selectedPricingPlan: action.payload,
        }
    },
    [types.SET_SELECTED_BUNDLE]: (state, action) => {
        return {
            ...state,
            selectedBundle: action.payload,
        }
    },
    [types.SET_PRICE_PLAN_FOR_HISTORY]: (state, action) => {
        return {
            ...state,
            selectedPricePlanForHistory: action.payload,
        }
    },
    [types.SET_PREV_SCREEN_FOR_HISTORY]: (state, action) => {
        return {
            ...state,
            prevScreenForHistory: action.payload,
        }
    },
    [types.CLEAR_PRICE_PLAN_HISTORY]: (state, action) => {
        return {
            ...state,
            selectedPricePlanForHistory: null,
            prevScreenForHistory: null,
        }
    },
    [types.SET_CREATE_BUNDLE_ERROR]: (state, action) => {
        return {
            ...state,
            bundleCreateError: false,
            bundleCreateErrorMessage: null,
        }
    },

    [types.REMOVE_TOAST1]: (state, action) => {
        return {
            ...state,
            showSuccessToast1: false,
            toastMessage1: "",
        }
    },

    [types.RESET_ALL]: (state, action) => {
        return {
            ...apiBundleManagementInitialState,
        }
    },

    [types.SET_SUB_PLAN]: (state, action) => {
        return {
            ...state,
            selectedSubPlan: action.payload,
        }
    },
    [types.CLEAR_SUB_PLAN]: (state, action) => {
        return {
            ...state,
            selectedSubPlan: null,
        }
    },
    [types.SET_DRAFT_SUB_PLAN_STATE]: (state, action) => {
        return {
            ...state,
            draftSubPlanState: action.payload,
        }
    },
    [types.CLEAR_DRAFT_SUB_PLAN_STATE]: (state, action) => {
        return {
            ...state,
            draftSubPlanState: null,
        }
    },

    [types.SET_SUB_PLAN_CONTAINER]: (state, action) => {
        const { id, name, description, quotaLimit, overages, apiEndpoints, channels } = action.payload;

        const newSubPlanContainers = { ...state.subPlanContainers };

        // If we are editing and the name changed, remove the old one
        if (state.selectedSubPlanContainer && state.selectedSubPlanContainer !== name && newSubPlanContainers[state.selectedSubPlanContainer]) {
            delete newSubPlanContainers[state.selectedSubPlanContainer];
        }

        newSubPlanContainers[name] = {
            id,
            name,
            description: description || "",
            quotaLimit: quotaLimit || "",
            overages: overages || "",
            endpoints: Object.values(apiEndpoints),
            channels: Object.values(channels),
        };

        return {
            ...state,
            subPlanContainers: newSubPlanContainers,
        }
    },
    [types.SET_SELECTED_SUB_PLAN_CONTAINER]: (state, action) => {
        return {
            ...state,
            selectedSubPlanContainer: action.payload,
        }
    },
    [types.CLEAR_SUB_PLAN_CONTAINER]: (state, action) => {
        return {
            ...state,
            subPlanContainers: {},
        }
    },
    [types.REMOVE_SUB_PLAN_CONTAINER]: (state, action) => {
        const { name } = action.payload;
        const { [name]: removed, ...remainingContainers } = state.subPlanContainers;
        return {
            ...state,
            subPlanContainers: remainingContainers,
        }
    },
    [types.GET_BUNDLE_API_ENDPOINTS_REQUEST]: (state, action) => {
        return {
            ...state,
            fetchingEndpointsAndChannels: true,
            errorFetchingEndpointsAndChannels: false,
            errorMessageFetchingEndpointsAndChannels: null,
            bundleApiEndpoints: {},
            bundleApiChannels: {},
            endpointsBundleId: null,
        }
    },
    [types.GET_BUNDLE_API_ENDPOINTS_RECEIVE]: (state, action) => {
        // we can keep endpoints and instance map here 
        const apiEndpointsOrChannels = action.payload.items || [];
        const ChannelsMap = {};
        const endpointsMap = {};
        apiEndpointsOrChannels.forEach((apiDetails) => {
            if (apiDetails.type === "rest") {
                const endpoints = apiDetails.operations || [];
                endpoints.forEach((endpoint) => {
                    endpointsMap[endpoint.endpointId] = {
                        ...endpoint,
                        version: apiDetails.version,
                        apiName: apiDetails.apiName,
                        apiType: "api",
                        instanceId: apiDetails.instanceId,
                    };
                })
            } else if (apiDetails.type === "async") {
                const channels = apiDetails.operations || [];
                channels.forEach((apiChannel) => {
                    const channel = apiChannel.channel || {};
                    ChannelsMap[channel.id] = {
                        ...channel,
                        version: apiDetails.version,
                        apiName: apiDetails.apiName,
                        apiType: "async-api",
                        instanceId: apiDetails.instanceId,
                    };
                })
            }
        });
        return {
            ...state,
            fetchingEndpointsAndChannels: false,
            errorFetchingEndpointsAndChannels: false,
            errorMessageFetchingEndpointsAndChannels: null,
            bundleApiEndpoints: endpointsMap,
            bundleApiChannels: ChannelsMap,
            endpointsBundleId: action.bundleId,
        }
    },
    [types.GET_BUNDLE_API_ENDPOINTS_ERROR]: (state, action) => {
        return {
            ...state,
            fetchingEndpointsAndChannels: false,
            errorFetchingEndpointsAndChannels: true,
            errorMessageFetchingEndpointsAndChannels: action.payload,
            bundleApiEndpoints: {},
            bundleApiChannels: {},
        }
    },

    [types.HANDLE_SUB_PLAN_TITLE_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                [state.selectedSubPlan]: {
                    ...state.bundlePlan[state.selectedSubPlan],
                    title: action.payload,
                }
            }
        }
    },
    [types.HANDLE_SUB_PLAN_BASE_VALUE_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                [state.selectedSubPlan]: {
                    ...state.bundlePlan[state.selectedSubPlan],
                    baseValue: action.payload,
                }
            }
        }
    },
    [types.HANDLE_SUB_PLAN_DESCRIPTION_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                [state.selectedSubPlan]: {
                    ...state.bundlePlan[state.selectedSubPlan],
                    description: action.payload,
                }
            },
        }
    },
    [types.CREATE_SUB_PLAN_CONTAINER]: (state, action) => {
        const containers = action.payload;
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                [state.selectedSubPlan]: {
                    ...state.bundlePlan[state.selectedSubPlan],
                    containers: [...containers],
                }
            }
        }
    },
    [types.HANDLE_SUB_PLAN_RADIO_CHANGE]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                [state.selectedSubPlan]: {
                    ...state.bundlePlan[state.selectedSubPlan],
                    radioValue: action.payload,
                }
            }
        }
    },
    [types.HANDLE_SUB_PLAN_CONTAINER_QUOTA_LIMIT_CHANGE]: (state, action) => {
        return {
            ...state,
        }
    },
    [types.HANDLE_SUB_PLAN_CONTAINER_OVERAGES_CHANGE]: (state, action) => {
        return {
            ...state,
        }
    },
    [types.HANDLE_SUB_PLAN_RESET_CHANGE]: (state, action) => {
        const { selectedSubPlan } = state;
        return {
            ...state,
            bundlePlan: {
                ...state.bundlePlan,
                [selectedSubPlan]: {
                    ...apiBundleManagementInitialState.bundlePlan[selectedSubPlan],
                }
            }
        }
    },
    [types.RESET_BUNDLE_PLAN_DETAILS]: (state, action) => {
        return {
            ...state,
            bundlePlan: {
                ...apiBundleManagementInitialState.bundlePlan,
            }
        }
    },
}

const ApiBundleManagementReducer = (state = apiBundleManagementInitialState, action) => {
    return reducerActions[action.type] ? reducerActions[action.type](state, action) : state
};

export default ApiBundleManagementReducer;